/**
 * Diffing helpers for the prompt editor's save modal.
 *
 * Two jobs:
 *   - `sameDraft` — order-independent deep-equality on the five versioned fields,
 *     so "Save version" can be disabled when the draft matches the loaded version
 *     byte-for-byte (config key order doesn't count as a change).
 *   - `draftToLines` + `diffLines` — render a GitHub-style line-by-line diff of
 *     the loaded version against what's about to be saved.
 *
 * This is display/UX only. The server still decides whether the content is
 * actually new (identical content doesn't cut a version — see handleSaveVersion).
 */

import { messageText } from 'features/prompts/lib/promptDraft';

/** Order-independent structural equality (object key order is not a change). */
export function deepEqual(a, b) {
  if (a === b) return true;
  if (a === null || b === null || a === undefined || b === undefined) return a === b;
  if (typeof a !== 'object' || typeof b !== 'object') return a === b;
  const aArr = Array.isArray(a);
  if (aArr !== Array.isArray(b)) return false;
  if (aArr) {
    if (a.length !== b.length) return false;
    return a.every((x, i) => deepEqual(x, b[i]));
  }
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => Object.prototype.hasOwnProperty.call(b, k) && deepEqual(a[k], b[k]));
}

/** The five fields a version is made of — everything Save version persists. */
export function sameDraft(a, b) {
  if (!a || !b) return false;
  return deepEqual(a.messages || [], b.messages || [])
    && deepEqual(a.model || {}, b.model || {})
    && deepEqual(a.tools || [], b.tools || [])
    && deepEqual(a.response_format ?? null, b.response_format ?? null)
    && deepEqual(a.variables || [], b.variables || []);
}

const jsonLines = (value) => JSON.stringify(value, null, 2).split('\n');

/**
 * Flatten a draft into a stable, human-readable line list — messages first
 * (role-headed), then any non-empty config sections. This is what gets diffed;
 * it deliberately mirrors what the editor shows, not the wire shape.
 */
export function draftToLines(draft) {
  if (!draft) return [];
  const lines = [];

  (draft.messages || []).forEach((m, idx) => {
    if (idx > 0) lines.push('');
    if (m?.type === 'placeholder') {
      lines.push(`@placeholder ${m.name || ''}`.trimEnd());
      return;
    }
    lines.push(`@${m?.role || 'user'}`);
    messageText(m).split('\n').forEach((l) => lines.push(l));
  });

  const model = draft.model || {};
  if (Object.keys(model).length) {
    lines.push('', '# model config');
    lines.push(...jsonLines(model));
  }
  const tools = draft.tools || [];
  if (tools.length) {
    lines.push('', '# tools');
    lines.push(...jsonLines(tools));
  }
  if (draft.response_format != null) {
    lines.push('', '# output format');
    lines.push(...jsonLines(draft.response_format));
  }
  const vars = draft.variables || [];
  if (vars.length) {
    lines.push('', '# variables');
    vars.forEach((v) => lines.push(`${v.name}: ${v.type || 'string'}${v.required ? ' (required)' : ''}`));
  }

  return lines;
}

/**
 * LCS line diff. Returns rows tagged 'ctx' (unchanged), 'del' (only in old) or
 * 'add' (only in new), in reading order. O(n·m) — fine for a prompt's line count.
 */
export function diffLines(oldLines, newLines) {
  const n = oldLines.length;
  const m = newLines.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[i][j] = oldLines[i] === newLines[j]
        ? dp[i + 1][j + 1] + 1
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const out = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (oldLines[i] === newLines[j]) {
      out.push({ type: 'ctx', text: oldLines[i] });
      i += 1; j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ type: 'del', text: oldLines[i] });
      i += 1;
    } else {
      out.push({ type: 'add', text: newLines[j] });
      j += 1;
    }
  }
  while (i < n) { out.push({ type: 'del', text: oldLines[i] }); i += 1; }
  while (j < m) { out.push({ type: 'add', text: newLines[j] }); j += 1; }
  return out;
}
